import { inject, Injectable } from '@angular/core';
import { pushToStoreArray } from '@sama/app-state';
import { assert } from '@sama/js-core';
import { withHistory } from '@sama/rxjs-core';
import { clamp } from 'lodash';
import { distinctUntilChanged, map } from 'rxjs';
import { UuidService } from '../../../cross-team-shared/misc-services/uuid.service';
import { EventTrackingService } from '../../../shared/event-tracking/event-tracking.service';
import { HighlightFeedback } from '../../../team-apis/otterdam/feedback';
import { FeedbackPersistenceService } from '../../feedback/services/feedback-persistence.service';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { SamplingWorkspaceStore } from '../../sampling-workspace/state/sampling-workspace-store';
import { HighlightMetadata } from '../types/highlight-state';
import { getWordCount } from './word-count';

@Injectable()
export class HighlightService {
  private readonly store = inject(TaskWorkspaceStore);
  private readonly highlightStore = this.store('highlightState');

  private uuidService = inject(UuidService);
  private sampleStore = inject(SamplingWorkspaceStore);
  private eventTrackingService = inject(EventTrackingService);
  private feedbackPersistenceService = inject(FeedbackPersistenceService);

  setInProgress(highlight: HighlightMetadata) {
    this.highlightStore('inProgress').set(highlight);
  }

  clearHighlighted() {
    this.highlightStore('highlighted').delete();
  }

  clearInProgress() {
    this.highlightStore('inProgress').delete();
  }

  clearSelected() {
    this.highlightStore('selected').delete();
  }

  saveInProgressHighlight(options: Partial<HighlightFeedback>) {
    const { inProgress } = this.store.state().highlightState;
    assert(inProgress, 'In progress highlight must be set before saving');

    const highlightFeedback: HighlightFeedback = {
      ...this.buildHighlightFeedback(inProgress),
      ...options,
    };
    this.feedbackPersistenceService.upsertHighlight$(highlightFeedback);

    this.store.batch(() => {
      pushToStoreArray(this.store('highlightFeedbacks'), highlightFeedback);
      this.highlightStore('inProgress').delete();
    });
    this.sampleStore.updateFeedbackCount(1);

    this.eventTrackingService.track({
      id: 'add_feedback_workspace',
      bucket: 'quality_feedback',
      type: 'object',
      sub_type: 'highlight',
      error_tag: highlightFeedback.errorTag,
      comment: highlightFeedback.comment,
      feedback_id: highlightFeedback.feedbackId,
    });
  }

  updateHighlight(highlightFeedback: HighlightFeedback) {
    const { highlightFeedbacks } = this.store.state();

    const index = highlightFeedbacks.findIndex(
      (feedback) => feedback.feedbackId === highlightFeedback.feedbackId,
    );
    assert(index !== -1, 'Highlight feedback must exist before updating');

    highlightFeedback.updatedAt = new Date().toISOString();
    this.feedbackPersistenceService.upsertHighlight$(highlightFeedback);

    this.store('highlightFeedbacks')(index).set(highlightFeedback);

    this.eventTrackingService.track({
      id: 'edit_feedback_workspace',
      bucket: 'quality_feedback',
      type: 'object',
      sub_type: 'highlight',
      error_tag: highlightFeedback.errorTag,
      comment: highlightFeedback.comment,
      feedback_id: highlightFeedback.feedbackId,
    });
  }

  deleteSelectedHighlight() {
    const { selected } = this.highlightStore.state();
    assert(selected, 'Selected highlight must be set before deleting');

    this.feedbackPersistenceService.deleteHighlight$(selected);
    this.store.batch(() => {
      const { highlightFeedbacks } = this.store.state();
      this.store('highlightFeedbacks').set(
        highlightFeedbacks.filter((feedback) => feedback !== selected),
      );
      this.highlightStore('selected').set(undefined);
    });
    this.sampleStore.updateFeedbackCount(-1);

    this.eventTrackingService.track({
      id: 'delete_feedback_workspace',
      bucket: 'quality_feedback',
      type: 'object',
      sub_type: 'highlight',
      error_tag: selected.errorTag,
      comment: selected.comment,
      feedback_id: selected.feedbackId,
    });
  }

  getResponseHighlights$(textObjectIndex: number) {
    return this.store('highlightFeedbacks').$.pipe(
      map((feedbacks) =>
        feedbacks.filter((feedback) => feedback.objectId === textObjectIndex),
      ),
      distinctUntilChanged(),
      withHistory(1),
      map(([current, previous]) => {
        const added = current.filter(
          (feedback) => !previous?.includes(feedback),
        );
        const removed =
          previous?.filter((feedback) => !current.includes(feedback)) ?? [];
        return [added, removed] as [HighlightFeedback[], HighlightFeedback[]];
      }),
    );
  }

  selectHighlight(highlightFeedback: HighlightFeedback) {
    this.highlightStore('selected').set(highlightFeedback);
  }

  selectFirstFeedback(textObjectIndex: number, includedTextIndex: number) {
    const highlightFeedback = this.findHighlightFeedback(
      textObjectIndex,
      includedTextIndex,
    );
    this.highlightStore('selected').set(highlightFeedback);
    return highlightFeedback;
  }

  highlightFirstFeedback(textObjectIndex: number, includedTextIndex: number) {
    const highlightFeedback = this.findHighlightFeedback(
      textObjectIndex,
      includedTextIndex,
    );
    this.highlightStore('highlighted').set(highlightFeedback);
    return highlightFeedback;
  }

  private findHighlightFeedback(
    textObjectIndex: number,
    includedTextIndex: number,
  ) {
    const { highlightFeedbacks } = this.store.state();
    return highlightFeedbacks.find(
      (feedback) =>
        feedback.objectId === textObjectIndex &&
        clamp(includedTextIndex, feedback.startIndex, feedback.endIndex) ===
          includedTextIndex,
    );
  }

  private buildHighlightFeedback(
    highlightMetadata: HighlightMetadata,
  ): HighlightFeedback {
    const timestamp = new Date().toISOString();

    return {
      feedbackId: this.uuidService.getUuidV4(),
      stepId: this.store.getLastSubmission().step_id,
      createdBy: this.store.state().currentUser.id,
      itemType: 'object_feedback',
      itemSubType: 'highlight',
      tagName: 'text',
      errorTag: 'Factuality',
      objectId: highlightMetadata.objectId,
      startIndex: highlightMetadata.startIndex,
      endIndex: highlightMetadata.endIndex,
      wordCount: getWordCount(highlightMetadata.text),
      createdAt: timestamp,
      updatedAt: timestamp,
    };
  }
}
